import type { FC } from 'react';
import { msg } from '@/services/isomorphic/I18NService';
import { Link } from '@/react/components/core-ui/Link';
import { observer } from 'mobx-react-lite';
import type { SearchBoxVM } from '@/react/view-models/SearchBoxVM';
import type { VoidCallback } from '@/type-utils';

import S from './styles.base.module.scss';
import { search_box_frame_didYouMean } from "@/lang/__generated__/ahnu/search_box_frame_didYouMean";

interface IInternalHighlightTextProps {
  /**
   * The text to highlight parts from.
   *
   * Needs to be formatted as described in {@link ISearchTermsSuggestion} to be properly highlighted.
   *
   * Since the format is not **actually** guaranteed, we will validate in runtime and fallback to the
   * text inside the brackets if it fails to match.
   */
  text: string;
}

/**
 * An internal component to highlight parts of a string.
 */
const InternalHighlightText: FC<IInternalHighlightTextProps> = ({ text }) => {
  /**
   * Extracts all bracketed segments and the spaces between them using regex matching.
   *
   * **IMPORTANT** -- This expects an input like described at {@link ISearchTermsSuggestion}. If it fails to match
   * it will return an array with the text inside `[]`, which is equivalent of a term with
   * no bold text as fallback.
   *
   * @example
   * "[mid]{form}" → ["[mid]", "{form}"]
   * "[normal] {bold}[text]" → ["[normal]", " ", "{bold}", "[text]"]
   * "Invalid" → ["[Invalid]"]
   */
  const parts = text.match(/\[.*?\]|\{.*?\}|\s+/g) ?? [`[${text}]`];

  return (
    <span className={S.highlightText}>
      {parts.map((part) => {
        // If the part is whitespace, preserve it as is
        if (part.trim() === '') {
          return part;
        }

        // If the part starts with `{` and ends with `}`, it is a bold text
        if (part.startsWith('{') && part.endsWith('}')) {
          return (
            <span key={`${text}-${part}`} className={S.bold}>
              {part.slice(1, -1)}
            </span>
          );
        }

        // Otherwise, it is a normal text
        return (
          <span key={`${text}-${part}`} className={S.normal}>
            {part.slice(1, -1)}
          </span>
        );
      })}
    </span>
  );
};

export interface IDidYouMeanProps {
  /**
   * The `SearchBoxVM` instance to be used.
   */
  vm: SearchBoxVM;

  /**
   * The function to be called when the user navigates to a new page.
   */
  onNavigate: VoidCallback;
}

/**
 * A component to display the `Did You Mean?` Section upon search.
 */
export const DidYouMean: FC<IDidYouMeanProps> = observer(
  ({ vm, onNavigate }) => {
    /**
     * From the search suggestions presented to the user, the `didYouMean` property
     * is the only one other one than the input search that we want to save to the user search history upon
     * clicking.
     *
     * See {@link https://deckersbrand.atlassian.net/browse/HP-4785?focusedCommentId=414761 | HP-4785} for more details.
     *
     * @param term - The term to save and navigate to.
     */
    const handleOnClick = (term: string): void => {
      vm.saveTermToHistory(term);

      onNavigate();
    };

    return (
      <>
        <h3 className={S.title}>{msg(search_box_frame_didYouMean)}</h3>
        <div className={S.didYouMean}>
          {vm.didYouMean.map(({ term, highlightPortion }) => (
            <Link
              key={highlightPortion}
              className={S.term}
              href={vm.encodeSearchResultsPageNavigationUrl(term)}
              onClick={() => handleOnClick(term)}
              variant="text"
            >
              <InternalHighlightText
                // If for some reason the highlightPortion is empty, we fallback to the term
                text={highlightPortion ?? term}
              />
            </Link>
          ))}
        </div>
      </>
    );
  }
);
